import React, { useRef, useState } from "react";
import styled from "styled-components";
import { useSnackbar } from "notistack";
import {
  FileText,
  DocumentsBundle,
  Calendar,
  Presentation,
  MonitorPlay,
  ArrowUpRight,
} from "../HearingBundles/components/Icons.js";
import theme from "../../../../assets/theme";
import COLORS from "../../../../assets/Colors";
import EpeService from "../../../../services/EpeService";
import useEpeAccess from "../../../../hooks/useEpeAccess";
import { getErrorMessage } from "../../../../helpers/functions";
import { showEpeWaiting } from "./epeWaitingPage";

export function CaseTabStrip({ active, onChange, caseDetails, view, setView }) {
  return (
    <Strip>
      {TABS.filter(
        (tab) =>
          !(
            caseDetails?.resolutionKind === "negotiation" &&
            tab.key === "hearings"
          ),
      ).map((tab) => {
        const Icon = tab.icon;
        return (
          <TabBtn
            key={tab.key}
            $active={active.key === tab.key}
            onClick={() => {
              onChange(tab);
              setView(false);
              if (tab.key !== "documents" && typeof window !== "undefined" && caseDetails?.id) {
                const kind = caseDetails?.resolutionKind || "arbitration";
                const target = `/${kind}/${caseDetails.id}`;
                const path = window.location.pathname || "";
                const idx = path.indexOf(target);
                const basePath = idx !== -1 ? path.substring(0, idx + target.length) : `/dashboard/case-manager/${kind}/${caseDetails.id}`;
                if (window.location.pathname !== basePath) {
                  window.history.pushState({ path: basePath }, "", basePath);
                }
              }
            }}
          >
            <Icon size={16} />
            <TabLabel>{tab.label}</TabLabel>
          </TabBtn>
        );
      })}
      <EpeEntryTab caseDetails={caseDetails} />
    </Strip>
  );
}

const TABS = [
  { key: "sheet", label: "Case Sheet", icon: FileText },
  { key: "documents", label: "Documents & Bundles", icon: DocumentsBundle },
  { key: "discovery", label: "Case Discovery", icon: FileText },
  { key: "hearings", label: "Hearings & Transcripts", icon: Calendar },
];

// Never empty: an empty (or JSON null) error body has no words of its own.
const errorText = (error) =>
  (error && getErrorMessage(error)) || getErrorMessage({});

/** "N documents sent to JustCue." plus the ones JustCue could not add. */
const documentsSentText = (documents) => {
  const sent = Number(documents?.ready) || 0;
  const skipped = Array.isArray(documents?.skipped) ? documents.skipped.length : 0;
  return `${sent} ${sent === 1 ? "document" : "documents"} sent to JustCue.${
    skipped > 0 ? ` ${skipped} could not be added (unsupported or failed).` : ""
  }`;
};

/**
 * A new tab, opened while the click still counts as the person's own (popup blockers), that says what is going
 * on (a loading page, epeWaitingPage.js) until JustAct answers with the JustCue link. null when the browser
 * blocked it.
 */
const openPendingWindow = (waiting) => {
  let win = null;
  try {
    win = window.open("", "_blank");
  } catch (e) {
    win = null;
  }
  if (!win) return null;
  showEpeWaiting(win, waiting);
  return win;
};

/**
 * The EPE (JustCue) entry point, after the tabs (it is not one of them): "Presentation" for a person with EPE
 * operator access, "Evidence Display" for everyone else. A click opens JustCue in a new browser tab; a
 * Presentation launch first sends all the case's documents to JustCue.
 */
function EpeEntryTab({ caseDetails }) {
  const { access, refresh } = useEpeAccess();
  const { enqueueSnackbar } = useSnackbar();
  const [launching, setLaunching] = useState(false);
  // A launch is in flight (a second click before the busy state renders opens nothing).
  const launchingRef = useRef(false);

  if (!access?.enabled || caseDetails?.resolutionKind === "negotiation") {
    return null;
  }

  const operator = !!access.canOperate;
  const target = operator ? "presentation" : "evidence";

  const launch = async () => {
    if (launchingRef.current) return;
    launchingRef.current = true;
    const win = openPendingWindow({
      presentation: operator,
      caseLabel: caseDetails?.id ? `Case ${caseDetails.id}` : "",
    });
    setLaunching(true);
    try {
      const res = await EpeService.launch(
        caseDetails.id,
        target,
        window.location.href,
      );
      const url = typeof res?.url === "string" ? res.url : "";
      if (!/^https?:\/\//i.test(url)) {
        if (win && !win.closed) win.close();
        enqueueSnackbar(getErrorMessage({}), { variant: "error" });
        return;
      }
      if (win) {
        // The person may have closed the waiting tab; then JustCue is not opened at all.
        if (!win.closed) {
          try {
            win.opener = null;
          } catch (e) {
            // Not every browser lets go of the opener; JustCue opens all the same.
          }
          win.location.href = url;
        }
      } else {
        window.location.assign(url);
      }
      if (target === "presentation" && res.documents) {
        enqueueSnackbar(documentsSentText(res.documents), {
          variant: "success",
        });
      }
    } catch (error) {
      if (win && !win.closed) win.close();
      enqueueSnackbar(errorText(error), { variant: "error" });
      // Operator access may have changed meanwhile (a 403 for Presentation).
      refresh();
    } finally {
      launchingRef.current = false;
      setLaunching(false);
    }
  };

  return (
    <EntryTabBtn
      type="button"
      onClick={launch}
      aria-disabled={launching || undefined}
      aria-busy={launching || undefined}
      title={
        operator
          ? "Open the presentation console in a new tab"
          : "Open the evidence display in a new tab"
      }
    >
      <EntryIcon aria-hidden="true">
        {operator ? <Presentation size={16} /> : <MonitorPlay size={16} />}
      </EntryIcon>
      <EntryLabel>{operator ? "Presentation" : "Evidence Display"}</EntryLabel>
      <EntryArrow aria-hidden="true">
        <ArrowUpRight size={14} />
      </EntryArrow>
    </EntryTabBtn>
  );
}

export const Strip = styled.nav`
  display: flex;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 10;

  /* Hide scrollbar */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  /* Smooth scrolling */
  scroll-behavior: smooth;
`;

export const TabBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.TRANSCRIPT_GRAY};
  border: none;
  background: transparent;
  border-bottom: 3px solid transparent;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s ease;
  cursor: pointer;
  min-height: 52px;

  ${({ $active }) =>
    $active &&
    `
    color: #00838c;
    font-weight: 600;
    border-bottom: 3px solid #00838c;
    background: #F0F9F8;
  `}

  @media (max-width: 480px) {
    padding: 12px 14px;
    font-size: 12px;
    min-height: 48px;
  }
`;

export const TabLabel = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  @media (max-width: 380px) {
    display: none;
  }
`;

// A tab in looks, never "active": the page does not change, JustCue opens beside it. While a launch is in
// flight it is aria-disabled (it keeps the focus) and ignores clicks.
const EntryTabBtn = styled(TabBtn)`
  color: ${COLORS.COLOR_DARK};

  &:hover:not([aria-disabled="true"]) {
    color: ${COLORS.BTN_GREEN};
    background: #f0f9f8;
  }

  &[aria-disabled="true"] {
    cursor: progress;
    opacity: 0.6;
  }
`;

const EntryIcon = styled.span`
  display: inline-flex;
  color: ${COLORS.BTN_GREEN};
`;

const EntryLabel = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const EntryArrow = styled.span`
  display: inline-flex;
  color: #94a3b8;
  transition: color 0.2s ease;

  ${EntryTabBtn}:hover:not([aria-disabled="true"]) & {
    color: ${COLORS.BTN_GREEN};
  }
`;
